// =============================================================================
// MAIN.JS - Script principal du thème Bathe
// =============================================================================
// Ce fichier contient toutes les fonctionnalités JavaScript du site :
// 1. Menu mobile avec sous-menus
// 2. Mega menu (desktop)
// 3. Header sticky (mobile + desktop)
// 4. Accordéons (bloc certification-info)
// 5. Bloc Action Checklist (onglets + localStorage)
// 6. Page Ressources (accordéons, panier, recherche, filtres, téléchargement ZIP)
// 7. Sidebar Critères Accordion (ouverture/fermeture des catégories)
// =============================================================================

// =============================================================================
// 1. MENU MOBILE - Injection des boutons toggle et retour
// =============================================================================
// Cette fonction ajoute dynamiquement :
// - Un bouton chevron (>) à côté de chaque lien de menu qui a des enfants
// - Un bouton "Retour" en haut de chaque sous-menu
// - Un titre dans chaque sous-menu (reprend le texte du lien parent)
// =============================================================================

const injectSubmenuToggles = () => {
  // Sélectionne tous les éléments de menu qui ont des sous-menus
  const menuItemsWithChildren = document.querySelectorAll('.menu-item-has-children');

  menuItemsWithChildren.forEach((item) => {
    // Évite d'ajouter un toggle s'il existe déjà
    if (item.querySelector('.submenu-toggle')) return;

    // Crée le bouton toggle avec l'icône chevron (flèche vers le bas)
    const toggle = document.createElement('button');
    toggle.className = 'submenu-toggle';
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-label', 'Toggle submenu');
    toggle.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
      </svg>
    `;

    // Insère le toggle juste après le lien du menu
    const links = item.querySelectorAll('a');
    if (links.length > 0) {
      const link = links[0]; // Le premier <a> est le lien direct
      link.parentNode.insertBefore(toggle, link.nextSibling);
    }

    // Cherche le sous-menu (ul.sub-menu) parmi les enfants directs
    let submenu = null;
    for (let child of item.children) {
      if (child.classList.contains('sub-menu')) {
        submenu = child;
        break;
      }
    }

    // Ajoute le bouton retour et le titre dans le sous-menu
    if (submenu && !submenu.querySelector('.submenu-back')) {
      // Récupère le texte du lien parent pour le titre
      const parentLinkText = links[0] ? links[0].textContent : 'Menu';

      // Crée le bouton "Retour" avec icône flèche gauche
      const backButton = document.createElement('button');
      backButton.className = 'submenu-back';
      backButton.setAttribute('aria-label', 'Retour');
      backButton.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
        </svg>
        <span>Retour</span>
      `;

      // Crée le titre du sous-menu
      const title = document.createElement('div');
      title.className = 'submenu-title';
      title.textContent = parentLinkText;

      // Insère le titre puis le bouton retour au début du sous-menu
      submenu.insertBefore(title, submenu.firstChild);
      submenu.insertBefore(backButton, submenu.firstChild);
    }
  });
};

// =============================================================================
// 2. MENU MOBILE - Gestion de l'ouverture/fermeture
// =============================================================================
// Cette fonction gère :
// - Le clic sur le bouton burger pour ouvrir/fermer le menu
// - L'overlay qui s'affiche derrière le menu
// - La fermeture du menu en cliquant dehors ou avec la touche Escape
// - L'ouverture des sous-menus via les boutons toggle
// - La fermeture des sous-menus via les boutons "Retour"
// =============================================================================

const initMobileMenu = () => {
  // Récupère les éléments nécessaires
  const menuToggle = document.querySelector('.mobile-menu-toggle'); // Bouton burger
  const menu = document.querySelector('.main-navigation'); // Le menu lui-même
  const overlay = document.querySelector('.site-header__nav-overlay'); // Fond sombre

  const closeButton = menu.querySelector('.main-navigation__close-button');

  // Si un élément manque, on arrête
  if (!menuToggle || !menu || !overlay) return;

  // Injecte les boutons toggle et retour dans les sous-menus
  injectSubmenuToggles();

  // ---------------------------------------------------------------------------
  // Fonction pour OUVRIR le menu
  // ---------------------------------------------------------------------------
  const openMenu = () => {
    menuToggle.setAttribute('aria-expanded', 'true');
    menu.classList.add('main-navigation--open');
    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('mobile-menu-open'); // Empêche le scroll du body
  };

  // ---------------------------------------------------------------------------
  // Fonction pour FERMER le menu
  // ---------------------------------------------------------------------------
  const closeMenu = () => {
    menuToggle.setAttribute('aria-expanded', 'false');
    menu.classList.remove('main-navigation--open');
    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('mobile-menu-open');

    // Ferme aussi tous les sous-menus ouverts
    const openSubmenus = menu.querySelectorAll('.sub-menu.is-open');
    openSubmenus.forEach((submenu) => {
      submenu.classList.remove('is-open');
    });

    // Réinitialise les boutons toggle des sous-menus
    const openToggles = menu.querySelectorAll('.submenu-toggle.is-open');
    openToggles.forEach((toggle) => {
      toggle.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
    });
  };

  // ---------------------------------------------------------------------------
  // Événement : clic sur le bouton burger
  // ---------------------------------------------------------------------------
  menuToggle.addEventListener('click', () => {
    const isExpanded = menuToggle.getAttribute('aria-expanded') === 'true';
    if (isExpanded) {
      closeMenu();
    } else {
      openMenu();
    }
  });

  // ---------------------------------------------------------------------------
  // Événement : clic sur le bouton fermer dans le panel nav
  // ---------------------------------------------------------------------------
  closeButton?.addEventListener('click', closeMenu);

  // ---------------------------------------------------------------------------
  // Événement : clic sur l'overlay (fond sombre) = ferme le menu
  // ---------------------------------------------------------------------------
  overlay.addEventListener('click', closeMenu);

  // ---------------------------------------------------------------------------
  // Événement : clic en dehors du menu = ferme le menu
  // ---------------------------------------------------------------------------
  document.addEventListener('click', (event) => {
    if (!menu.contains(event.target) && !menuToggle.contains(event.target)) {
      closeMenu();
    }
  });

  // ---------------------------------------------------------------------------
  // Événement : touche Escape = ferme le menu
  // ---------------------------------------------------------------------------
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && menu.classList.contains('main-navigation--open')) {
      closeMenu();
    }
  });

  // ---------------------------------------------------------------------------
  // Événement : clic sur un bouton toggle de sous-menu = OUVRE le sous-menu
  // ---------------------------------------------------------------------------
  menu.addEventListener('click', (event) => {
    const toggle = event.target.closest('.submenu-toggle');
    if (!toggle) return;

    event.preventDefault();
    event.stopPropagation();

    const parentItem = toggle.closest('.menu-item-has-children');

    // Trouve le sous-menu enfant direct
    let submenu = null;
    for (let child of parentItem.children) {
      if (child.classList.contains('sub-menu')) {
        submenu = child;
        break;
      }
    }

    if (!submenu) return;

    // Ouvre le sous-menu en mode "modal" (plein écran mobile)
    submenu.classList.add('is-open');
    toggle.classList.add('is-open');
    toggle.setAttribute('aria-expanded', 'true');

    // Cache le bouton burger quand un sous-menu est ouvert
    menuToggle.classList.add('submenu-active');
  });

  // ---------------------------------------------------------------------------
  // Événement : clic sur le bouton "Retour" = FERME le sous-menu
  // ---------------------------------------------------------------------------
  menu.addEventListener('click', (event) => {
    const backButton = event.target.closest('.submenu-back');
    if (!backButton) return;

    event.preventDefault();
    event.stopPropagation();

    // Trouve le sous-menu qui contient ce bouton retour
    const submenu = backButton.closest('.sub-menu');
    if (!submenu) return;

    // Trouve l'élément parent du sous-menu
    const parentItem = submenu.parentElement;
    if (!parentItem) return;

    // Trouve le bouton toggle correspondant
    let toggle = null;
    for (let child of parentItem.children) {
      if (child.classList.contains('submenu-toggle')) {
        toggle = child;
        break;
      }
    }

    // Ferme le sous-menu
    submenu.classList.remove('is-open');
    if (toggle) {
      toggle.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
    }

    // Réaffiche le bouton burger si plus aucun sous-menu n'est ouvert
    const openSubmenus = menu.querySelectorAll('.sub-menu.is-open');
    if (openSubmenus.length === 0) {
      menuToggle.classList.remove('submenu-active');
    }
  });
};

// =============================================================================
// 3. MEGA MENU - Menu déroulant à deux colonnes (DESKTOP UNIQUEMENT)
// =============================================================================
// Transforme un menu WordPress standard en mega menu :
// - Colonne gauche : liste des catégories
// - Colonne droite : liste des critères de la catégorie survolée
// Le premier élément est actif par défaut
// =============================================================================

const initMegaMenu = () => {
  // Ne s'exécute que sur desktop (écran >= 1025px)
  if (window.innerWidth < 1025) return;

  // Trouve tous les éléments de menu marqués comme mega-menu
  const megaMenus = document.querySelectorAll('.menu-item-mega-menu');

  megaMenus.forEach((megaMenu) => {
    const submenu = megaMenu.querySelector(':scope > .sub-menu');
    if (!submenu) return;

    // Ajoute un chevron au lien du mega-menu (visible uniquement sur desktop via CSS)
    const megaMenuLink = megaMenu.querySelector(':scope > a');
    if (megaMenuLink && !megaMenuLink.querySelector('.mega-menu-chevron')) {
      const chevron = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      chevron.setAttribute('class', 'mega-menu-chevron');
      chevron.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      chevron.setAttribute('fill', 'none');
      chevron.setAttribute('viewBox', '0 0 24 24');
      chevron.setAttribute('stroke-width', '2');
      chevron.setAttribute('stroke', 'currentColor');
      chevron.setAttribute('aria-hidden', 'true');
      chevron.innerHTML = '<path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />';
      megaMenuLink.appendChild(chevron);
    }

    // Vérifie si la restructuration a déjà été faite
    if (submenu.querySelector('.mega-menu-wrapper')) return;

    // Récupère tous les éléments de premier niveau qui ont des enfants
    const firstLevelItems = submenu.querySelectorAll(':scope > .menu-item-has-children');
    if (firstLevelItems.length === 0) return;

    // Crée la structure du mega menu
    const wrapper = document.createElement('div');
    wrapper.className = 'mega-menu-wrapper';

    // Colonne gauche : les catégories
    const leftColumn = document.createElement('div');
    leftColumn.className = 'mega-menu-categories';

    // Colonne droite : le contenu (critères)
    const rightColumn = document.createElement('div');
    rightColumn.className = 'mega-menu-content';

    // Traite chaque catégorie
    firstLevelItems.forEach((item, index) => {
      // Clone le lien de la catégorie
      const link = item.querySelector(':scope > a');
      const categoryItem = document.createElement('div');
      categoryItem.className = 'mega-menu-category-item';

      const categoryLink = link.cloneNode(true);
      categoryLink.className = 'mega-menu-category-link';
      categoryItem.appendChild(categoryLink);

      leftColumn.appendChild(categoryItem);

      // Récupère le sous-menu de cette catégorie (les critères)
      const itemSubmenu = item.querySelector(':scope > .sub-menu');
      if (itemSubmenu) {
        categoryItem.dataset.index = index;

        // Clone le sous-menu pour la colonne droite
        const submenuClone = itemSubmenu.cloneNode(true);
        submenuClone.className = 'mega-menu-criteria';
        submenuClone.dataset.index = index;
        submenuClone.style.display = 'none'; // Caché par défaut
        rightColumn.appendChild(submenuClone);
      }

      // Supprime l'élément original
      item.remove();
    });

    // Affiche le premier sous-menu par défaut
    const firstSubmenu = rightColumn.querySelector('.mega-menu-criteria[data-index="0"]');
    if (firstSubmenu) {
      firstSubmenu.style.display = 'flex';
      leftColumn.querySelector('.mega-menu-category-item[data-index="0"]')?.classList.add('is-active');
    }

    // Assemble la structure
    wrapper.appendChild(leftColumn);
    wrapper.appendChild(rightColumn);

    // Remplace le contenu du sous-menu par la nouvelle structure
    submenu.innerHTML = '';
    submenu.appendChild(wrapper);

    // ---------------------------------------------------------------------------
    // Événement : survol d'une catégorie = affiche ses critères
    // ---------------------------------------------------------------------------
    const categoryItems = leftColumn.querySelectorAll('.mega-menu-category-item');
    categoryItems.forEach((categoryItem) => {
      categoryItem.addEventListener('mouseenter', () => {
        const index = categoryItem.dataset.index;

        // Retire la classe active de toutes les catégories
        categoryItems.forEach((item) => item.classList.remove('is-active'));
        categoryItem.classList.add('is-active');

        // Cache tous les sous-menus de critères
        const allCriteria = rightColumn.querySelectorAll('.mega-menu-criteria');
        allCriteria.forEach((criteria) => {
          criteria.style.display = 'none';
        });

        // Affiche le sous-menu correspondant
        const targetCriteria = rightColumn.querySelector(`.mega-menu-criteria[data-index="${index}"]`);
        if (targetCriteria) {
          targetCriteria.style.display = 'flex';
        }
      });
    });
  });
};

// Réinitialise le mega menu lors du redimensionnement de la fenêtre
// (avec un délai pour éviter les appels trop fréquents)
let resizeTimeout;
window.addEventListener('resize', () => {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => {
    initMegaMenu();
  }, 250);
});

// =============================================================================
// 4. STICKY HEADER - Barre de navigation fixe au scroll (MOBILE + DESKTOP)
// =============================================================================
// Clone la barre de navigation et la fixe en haut de l'écran quand :
// - L'utilisateur a scrollé au-delà du header
// - L'utilisateur scroll vers le haut (apparaît)
// - L'utilisateur scroll vers le bas (disparaît)
// =============================================================================

const initStickyHeader = () => {
  // Désactive le sticky header sur les pages critères
  const isSingleCritere = document.querySelector('.single-critere');
  const isCriteresArchive = document.querySelector('.criteres-archive');
  if (isSingleCritere || isCriteresArchive) return;

  const siteHeader = document.querySelector('.site-header');
  const topBar = document.querySelector('.site-header__top-bar');
  if (!topBar || !siteHeader) return;

  // Calcule la position où la barre sticky doit apparaître
  const headerOffset = siteHeader.offsetTop + siteHeader.offsetHeight;

  // Clone la barre de navigation pour créer la version sticky
  const stickyBar = topBar.cloneNode(true);
  stickyBar.classList.add('site-header__top-bar--sticky');
  document.body.appendChild(stickyBar);

  // Supprime les anciens boutons toggle/retour (leurs événements ne fonctionneront pas)
  const clonedToggles = stickyBar.querySelectorAll('.submenu-toggle');
  const clonedBackButtons = stickyBar.querySelectorAll('.submenu-back');
  const clonedTitles = stickyBar.querySelectorAll('.submenu-title');
  clonedToggles.forEach((toggle) => toggle.remove());
  clonedBackButtons.forEach((btn) => btn.remove());
  clonedTitles.forEach((title) => title.remove());

  // Réinjecte les boutons toggle pour la barre sticky
  const stickyMenuItems = stickyBar.querySelectorAll('.menu-item-has-children');
  stickyMenuItems.forEach((item) => {
    // Crée le bouton toggle
    const toggle = document.createElement('button');
    toggle.className = 'submenu-toggle';
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-label', 'Toggle submenu');
    toggle.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
      </svg>
    `;

    // Insère après le lien
    const link = item.querySelector(':scope > a');
    if (link) {
      link.parentNode.insertBefore(toggle, link.nextSibling);
    }

    // Ajoute bouton retour et titre dans le sous-menu
    const submenu = item.querySelector(':scope > .sub-menu');
    if (submenu) {
      const parentLinkText = link ? link.textContent : 'Menu';

      const backButton = document.createElement('button');
      backButton.className = 'submenu-back';
      backButton.setAttribute('aria-label', 'Retour');
      backButton.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
        </svg>
        <span>Retour</span>
      `;

      const title = document.createElement('div');
      title.className = 'submenu-title';
      title.textContent = parentLinkText;

      submenu.insertBefore(title, submenu.firstChild);
      submenu.insertBefore(backButton, submenu.firstChild);
    }
  });

  // ---------------------------------------------------------------------------
  // Réinitialise les événements du menu mobile pour la barre sticky
  // ---------------------------------------------------------------------------
  const stickyMenuToggle = stickyBar.querySelector('.mobile-menu-toggle');
  const stickyMenu = stickyBar.querySelector('.main-navigation');
  const originalOverlay = document.querySelector('.site-header__nav-overlay');

  if (stickyMenuToggle && stickyMenu) {
    // Clic sur le bouton burger de la barre sticky
    stickyMenuToggle.addEventListener('click', () => {
      const isExpanded = stickyMenuToggle.getAttribute('aria-expanded') === 'true';
      if (isExpanded) {
        stickyMenuToggle.setAttribute('aria-expanded', 'false');
        stickyMenu.classList.remove('main-navigation--open');
        originalOverlay?.classList.remove('is-open');
        document.body.classList.remove('mobile-menu-open');
      } else {
        stickyMenuToggle.setAttribute('aria-expanded', 'true');
        stickyMenu.classList.add('main-navigation--open');
        originalOverlay?.classList.add('is-open');
        document.body.classList.add('mobile-menu-open');
      }
    });

    // Gestion des sous-menus dans la barre sticky
    stickyMenu.addEventListener('click', (event) => {
      const toggle = event.target.closest('.submenu-toggle');
      if (!toggle) return;

      event.preventDefault();
      event.stopPropagation();

      const parentItem = toggle.closest('.menu-item-has-children');
      const submenu = parentItem?.querySelector(':scope > .sub-menu');

      if (!submenu) return;

      submenu.classList.add('is-open');
      toggle.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
      stickyMenuToggle.classList.add('submenu-active');
    });

    // Bouton retour dans la barre sticky
    stickyMenu.addEventListener('click', (event) => {
      const backButton = event.target.closest('.submenu-back');
      if (!backButton) return;

      event.preventDefault();
      event.stopPropagation();

      const submenu = backButton.closest('.sub-menu');
      if (!submenu) return;

      const parentItem = submenu.parentElement;
      const toggle = parentItem?.querySelector(':scope > .submenu-toggle');

      submenu.classList.remove('is-open');
      if (toggle) {
        toggle.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
      }

      const openSubmenus = stickyMenu.querySelectorAll('.sub-menu.is-open');
      if (openSubmenus.length === 0) {
        stickyMenuToggle.classList.remove('submenu-active');
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Gestion du scroll : affiche/cache la barre sticky
  // ---------------------------------------------------------------------------
  let lastScrollY = window.scrollY;
  let isVisible = false;
  const scrollThreshold = 10; // Distance minimum de scroll pour déclencher un changement

  const handleScroll = () => {
    const currentScrollY = window.scrollY;
    const scrollDelta = currentScrollY - lastScrollY; // Positif = scroll vers le bas

    // Si on n'a pas dépassé le header, on cache la barre sticky
    if (currentScrollY <= headerOffset) {
      if (isVisible) {
        stickyBar.classList.remove('site-header__top-bar--visible');
        isVisible = false;
      }
    } else if (scrollDelta > scrollThreshold) {
      // Scroll vers le bas : CACHE la barre sticky
      if (isVisible) {
        stickyBar.classList.remove('site-header__top-bar--visible');
        isVisible = false;
      }
    } else if (scrollDelta < -scrollThreshold) {
      // Scroll vers le haut : AFFICHE la barre sticky
      if (!isVisible) {
        stickyBar.classList.add('site-header__top-bar--visible');
        isVisible = true;
      }
    }

    lastScrollY = currentScrollY;
  };

  // Écoute les événements de scroll (passive pour de meilleures performances)
  window.addEventListener('scroll', handleScroll, { passive: true });
};

// =============================================================================
// 5. ACCORDÉON - Bloc Certification Info
// =============================================================================
// Gère l'ouverture/fermeture des items d'accordéon dans le bloc certification.
// Un seul item peut être ouvert à la fois (comportement "accordion" classique).
// =============================================================================

const initCertificationAccordion = () => {
  const accordions = document.querySelectorAll('.certification-info-block__accordion');

  accordions.forEach((accordion) => {
    const items = accordion.querySelectorAll('.certification-info-block__accordion-item');
    const summaries = accordion.querySelectorAll('.certification-info-block__accordion-summary');

    summaries.forEach((summary) => {
      summary.addEventListener('click', () => {
        const parentItem = summary.closest('.certification-info-block__accordion-item');
        const isOpen = parentItem.classList.contains('certification-info-block__accordion-item--open');

        // Ferme TOUS les items
        items.forEach((item) => {
          item.classList.remove('certification-info-block__accordion-item--open');
          const itemSummary = item.querySelector('.certification-info-block__accordion-summary');
          if (itemSummary) {
            itemSummary.setAttribute('aria-expanded', 'false');
          }
        });

        // Si l'item cliqué était fermé, on l'ouvre
        if (!isOpen) {
          parentItem.classList.add('certification-info-block__accordion-item--open');
          summary.setAttribute('aria-expanded', 'true');
        }
      });
    });
  });
};

// =============================================================================
// 6. BLOC ACTION CHECKLIST - Onglets + Sauvegarde des checkboxes
// =============================================================================
// Ce bloc permet à l'utilisateur de cocher des critères selon son parcours
// (Labellisation simple ou Qualiopi).
// Les états des checkboxes sont sauvegardés dans le localStorage du navigateur
// pour être persistants entre les visites.
// =============================================================================

const initActionChecklist = () => {
  const blocks = document.querySelectorAll('.action-checklist');

  blocks.forEach((block) => {
    // Récupère l'ID unique du bloc (basé sur l'ID de la page)
    const blockId = block.dataset.blockId || 'action-checklist';
    const tabs = block.querySelectorAll('.action-checklist__tab');
    const panels = block.querySelectorAll('.action-checklist__panel');
    const checkboxes = block.querySelectorAll('.action-checklist__checkbox');

    // -------------------------------------------------------------------------
    // Gestion des ONGLETS
    // -------------------------------------------------------------------------
    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const targetTab = tab.dataset.tab; // "simple" ou "qualiopi"

        // Retire la classe active de tous les onglets
        tabs.forEach((t) => {
          t.classList.remove('action-checklist__tab--active');
          t.setAttribute('aria-selected', 'false');
        });
        // Active l'onglet cliqué
        tab.classList.add('action-checklist__tab--active');
        tab.setAttribute('aria-selected', 'true');

        // Affiche/cache les panneaux correspondants
        panels.forEach((panel) => {
          const panelTab = panel.dataset.tabPanel;
          if (panelTab === targetTab) {
            panel.classList.add('action-checklist__panel--active');
            panel.removeAttribute('hidden');
          } else {
            panel.classList.remove('action-checklist__panel--active');
            panel.setAttribute('hidden', '');
          }
        });
      });
    });

    // -------------------------------------------------------------------------
    // Gestion des CHECKBOXES avec localStorage
    // -------------------------------------------------------------------------
    checkboxes.forEach((checkbox) => {
      // Crée une clé unique pour chaque checkbox (blockId + storageKey)
      const storageKey = `${blockId}-${checkbox.dataset.storageKey}`;

      // Charge l'état sauvegardé
      const savedState = localStorage.getItem(storageKey);
      if (savedState === 'true') {
        checkbox.checked = true;
      }

      // Sauvegarde l'état à chaque changement
      checkbox.addEventListener('change', () => {
        localStorage.setItem(storageKey, checkbox.checked.toString());
      });
    });

    // -------------------------------------------------------------------------
    // Empêche le clic sur le lien de cocher la checkbox
    // -------------------------------------------------------------------------
    const checkboxLinks = block.querySelectorAll('.action-checklist__checkbox-text');
    checkboxLinks.forEach((link) => {
      link.addEventListener('click', (event) => {
        event.stopPropagation(); // Le clic sur le lien n'affecte pas la checkbox
      });
    });
  });
};

// =============================================================================
// 7. PAGE RESSOURCES - Accordéons, Panier, Recherche, Filtres, ZIP
// =============================================================================
// Cette page permet aux utilisateurs de :
// - Parcourir les ressources organisées par critères (accordéons)
// - Sélectionner des documents à télécharger (panier flottant)
// - Rechercher des documents par titre
// - Filtrer par catégorie de critère
// - Télécharger tous les documents sélectionnés en ZIP
// =============================================================================

const initResourcesPage = () => {
  const page = document.querySelector('.resources-page');
  if (!page) return; // Cette page n'existe pas, on arrête

  // ---------------------------------------------------------------------------
  // CONFIGURATION - Clé localStorage pour la persistance du panier
  // ---------------------------------------------------------------------------
  const STORAGE_KEY = 'ressources-cart-jemelabellise';

  // ---------------------------------------------------------------------------
  // ÉTAT DU PANIER - Chargé depuis localStorage ou tableau vide
  // ---------------------------------------------------------------------------
  const savedCart = localStorage.getItem(STORAGE_KEY);
  const cart = savedCart ? JSON.parse(savedCart) : [];
  // Structure d'un item dans le panier :
  // { id, title, file, critereNumber, critereTitle }

  // ---------------------------------------------------------------------------
  // ÉLÉMENTS DOM - Références aux éléments de la page
  // ---------------------------------------------------------------------------
  const cartElement = page.querySelector('.resources-page__cart'); // Conteneur du panier
  const cartToggle = page.querySelector('.resources-page__cart-toggle'); // Bouton pour ouvrir/fermer
  const cartContent = page.querySelector('.resources-page__cart-content'); // Contenu du panier
  const cartList = page.querySelector('.resources-page__cart-list'); // Liste des items
  const cartCount = page.querySelector('.resources-page__cart-count'); // Badge avec le nombre
  const cartDownload = page.querySelector('.resources-page__cart-download'); // Bouton télécharger

  // Éléments de recherche et filtre
  const searchInput = page.querySelector('.resources-page__search-input');
  const filterToggle = page.querySelector('.resources-page__filter-toggle');
  const filterDropdown = page.querySelector('.resources-page__filter-dropdown');
  const filterCheckboxes = page.querySelectorAll('.resources-page__filter-option input');

  // En-têtes des accordéons (cliquables pour ouvrir/fermer)
  const critereHeaders = page.querySelectorAll('.resources-page__critere-header');

  // ---------------------------------------------------------------------------
  // FONCTION : Sauvegarder le panier dans localStorage
  // ---------------------------------------------------------------------------
  const saveCart = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cart));
  };

  // ---------------------------------------------------------------------------
  // FONCTION : Restaurer les sélections visuelles au chargement de la page
  // ---------------------------------------------------------------------------
  const restoreSelections = () => {
    cart.forEach((item) => {
      const card = page.querySelector(
        `.resources-page__document-card[data-document-id="${item.id}"]`
      );
      if (card) {
        card.classList.add('resources-page__document-card--selected');
      }
    });
  };

  // ===========================================================================
  // ACCORDÉONS - Ouverture/fermeture des critères
  // ===========================================================================
  critereHeaders.forEach((header) => {
    header.addEventListener('click', () => {
      const accordion = header.closest('.resources-page__critere-accordion');
      const isOpen = accordion.classList.contains('resources-page__critere-accordion--open');

      if (isOpen) {
        // Ferme l'accordéon
        accordion.classList.remove('resources-page__critere-accordion--open');
        header.setAttribute('aria-expanded', 'false');
      } else {
        // Ouvre l'accordéon
        accordion.classList.add('resources-page__critere-accordion--open');
        header.setAttribute('aria-expanded', 'true');
      }
    });
  });

  // ===========================================================================
  // PANIER - Fonctionnalités
  // ===========================================================================

  // ---------------------------------------------------------------------------
  // FONCTION : Retirer un item du panier (appelée depuis le bouton X dans la modale)
  // ---------------------------------------------------------------------------
  const removeFromCart = (documentId) => {
    const index = cart.findIndex((item) => item.id === documentId);
    if (index > -1) {
      cart.splice(index, 1); // Supprime l'item du tableau

      // Met à jour l'état visuel de la carte du document
      const card = page.querySelector(
        `.resources-page__document-card[data-document-id="${documentId}"]`
      );
      if (card) {
        card.classList.remove('resources-page__document-card--selected');
      }

      updateCartUI(); // Met à jour l'interface
      saveCart(); // Sauvegarde dans localStorage
    }
  };

  // ---------------------------------------------------------------------------
  // Événement : clic sur le bouton toggle du panier (ouvre/ferme la modale)
  // ---------------------------------------------------------------------------
  if (cartToggle && cartElement) {
    cartToggle.addEventListener('click', () => {
      const isOpen = cartElement.classList.contains('resources-page__cart--open');

      if (isOpen) {
        cartElement.classList.remove('resources-page__cart--open');
        cartToggle.setAttribute('aria-expanded', 'false');
        cartContent.setAttribute('hidden', '');
      } else {
        cartElement.classList.add('resources-page__cart--open');
        cartToggle.setAttribute('aria-expanded', 'true');
        cartContent.removeAttribute('hidden');
      }
    });
  }

  // ---------------------------------------------------------------------------
  // FONCTION : Mettre à jour l'interface du panier
  // ---------------------------------------------------------------------------
  const updateCartUI = () => {
    // Met à jour le badge avec le nombre d'items
    if (cartCount) {
      cartCount.textContent = cart.length;
    }

    // Met à jour l'attribut data-count pour le styling CSS
    if (cartElement) {
      cartElement.dataset.count = cart.length;
    }

    // Reconstruit la liste des items dans le panier
    if (cartList) {
      cartList.innerHTML = ''; // Vide la liste

      // Groupe les items par critère (pour un affichage organisé)
      const grouped = {};
      cart.forEach((item) => {
        const key = item.critereTitle;
        if (!grouped[key]) {
          grouped[key] = [];
        }
        grouped[key].push(item);
      });

      // Crée le HTML pour chaque groupe (catégorie de critère)
      Object.keys(grouped).forEach((category) => {
        // Conteneur de la catégorie
        const categoryEl = document.createElement('div');
        categoryEl.className = 'resources-page__cart-category';

        // Titre de la catégorie (ex: "1.1 : Informations du public")
        const categoryTitle = document.createElement('div');
        categoryTitle.className = 'resources-page__cart-item-category';
        categoryTitle.textContent = category;
        categoryEl.appendChild(categoryTitle);

        // Liste des documents de cette catégorie (avec puces)
        const itemsList = document.createElement('ul');
        itemsList.className = 'resources-page__cart-items-list';

        grouped[category].forEach((item) => {
          // Élément de liste pour chaque document
          const itemEl = document.createElement('li');
          itemEl.className = 'resources-page__cart-item';

          // Titre du document
          const itemTitle = document.createElement('span');
          itemTitle.className = 'resources-page__cart-item-title';
          itemTitle.textContent = item.title;

          // Bouton de suppression (X)
          const removeBtn = document.createElement('button');
          removeBtn.className = 'resources-page__cart-item-remove';
          removeBtn.setAttribute('aria-label', 'Retirer ' + item.title);
          removeBtn.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          `;

          // Événement : clic sur X = retire l'item du panier
          removeBtn.addEventListener('click', () => {
            removeFromCart(item.id);
          });

          itemEl.appendChild(itemTitle);
          itemEl.appendChild(removeBtn);
          itemsList.appendChild(itemEl);
        });

        categoryEl.appendChild(itemsList);
        cartList.appendChild(categoryEl);
      });
    }

    // Active/désactive le bouton de téléchargement selon le contenu du panier
    if (cartDownload) {
      cartDownload.disabled = cart.length === 0;
    }
  };

  // ---------------------------------------------------------------------------
  // FONCTION : Ajouter ou retirer un document du panier (toggle)
  // ---------------------------------------------------------------------------
  const toggleCartItem = (documentCard, button) => {
    // Récupère les données du document depuis les attributs data-*
    const docId = documentCard.dataset.documentId;
    const docTitle = documentCard.dataset.documentTitle;
    const docFile = documentCard.dataset.documentFile;
    const critereTitle = documentCard.dataset.critereTitle;
    const fileId = button?.dataset.fileId; // Récupère le file_id depuis le bouton

    // Vérifie si le document est déjà dans le panier
    const existingIndex = cart.findIndex((item) => item.id === docId);

    if (existingIndex > -1) {
      // Le document est dans le panier → on le RETIRE
      cart.splice(existingIndex, 1);
      documentCard.classList.remove('resources-page__document-card--selected');
    } else {
      // Le document n'est pas dans le panier → on l'AJOUTE
      cart.push({
        id: docId,
        title: docTitle,
        file: docFile,
        critereTitle: critereTitle,
        fileId: fileId, // Stocke le file_id directement
      });
      documentCard.classList.add('resources-page__document-card--selected');
    }

    updateCartUI();
    saveCart();
  };

  // ---------------------------------------------------------------------------
  // Événement : clic sur le bouton "Sélectionner" d'un document
  // ---------------------------------------------------------------------------
  const documentButtons = page.querySelectorAll('.resources-page__document-button');
  documentButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const card = button.closest('.resources-page__document-card');
      toggleCartItem(card, button);
    });
  });

  // ---------------------------------------------------------------------------
  // Événement : clic sur les boutons de présélection (resources-intro)
  // ---------------------------------------------------------------------------
  const presetButtons = document.querySelectorAll('.resources-intro__preset-button');
  presetButtons.forEach((presetButton) => {
    presetButton.addEventListener('click', () => {
      const presetIds = JSON.parse(presetButton.dataset.presetIds || '[]').map(String);

      // Vide le panier et retire toutes les sélections visuelles
      cart.length = 0;
      page.querySelectorAll('.resources-page__document-card--selected').forEach((card) => {
        card.classList.remove('resources-page__document-card--selected');
      });

      // Ajoute chaque document présélectionné au panier
      presetIds.forEach((id) => {
        const card = page.querySelector(`.resources-page__document-card[data-document-id="${id}"]`);
        if (!card) return;

        const button = card.querySelector('.resources-page__document-button');
        cart.push({
          id: card.dataset.documentId,
          title: card.dataset.documentTitle,
          file: card.dataset.documentFile,
          critereTitle: card.dataset.critereTitle,
          fileId: button?.dataset.fileId ?? null,
        });
        card.classList.add('resources-page__document-card--selected');

        // Ouvre l'accordéon parent si fermé
        const accordion = card.closest('.resources-page__critere-accordion');
        if (accordion && !accordion.classList.contains('resources-page__critere-accordion--open')) {
          accordion.classList.add('resources-page__critere-accordion--open');
          const header = accordion.querySelector('.resources-page__critere-header');
          if (header) header.setAttribute('aria-expanded', 'true');
        }
      });

      updateCartUI();
      saveCart();

      // Ouvre le panier
      if (cartElement && cartContent && cartToggle) {
        cartElement.classList.add('resources-page__cart--open');
        cartToggle.setAttribute('aria-expanded', 'true');
        cartContent.removeAttribute('hidden');
      }

      // Scroll vers les documents
      const firstCard = page.querySelector('.resources-page__content');
      if (firstCard) {
        firstCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  });

  // ---------------------------------------------------------------------------
  // Événement : clic sur le bouton "Télécharger tout"
  // ---------------------------------------------------------------------------
  if (cartDownload) {
    cartDownload.addEventListener('click', async () => {
      if (cart.length === 0) return;

      // Si un seul fichier, téléchargement direct (pas besoin de ZIP)
      if (cart.length === 1) {
        const link = document.createElement('a');
        link.href = cart[0].file;
        link.download = '';
        link.click();
        return;
      }

      // Pour plusieurs fichiers, on crée un ZIP via AJAX
      // Vérifie si les données AJAX sont disponibles (injectées par WordPress)
      if (typeof batheAjax === 'undefined') {
        // Fallback : télécharge les fichiers un par un (si AJAX non disponible)
        cart.forEach((item, index) => {
          setTimeout(() => {
            const link = document.createElement('a');
            link.href = item.file;
            link.download = '';
            link.click();
          }, index * 500); // Délai entre chaque téléchargement
        });
        return;
      }

      // Affiche l'état de chargement
      const originalText = cartDownload.innerHTML;
      cartDownload.innerHTML = 'Création du ZIP...';
      cartDownload.disabled = true;

      try {
        // Récupère les IDs des fichiers directement depuis le panier
        const fileIds = cart
          .map((item) => item.fileId)
          .filter(Boolean); // Filtre les valeurs null/undefined

        // Prépare les données pour la requête AJAX
        const formData = new FormData();
        formData.append('action', 'bathe_download_zip'); // Action WordPress
        formData.append('nonce', batheAjax.nonce); // Sécurité
        fileIds.forEach((id) => formData.append('file_ids[]', id));

        // Envoie la requête au serveur
        const response = await fetch(batheAjax.ajaxUrl, {
          method: 'POST',
          body: formData,
        });

        const data = await response.json();

        if (data.success) {
          // Télécharge le fichier ZIP créé par le serveur
          const link = document.createElement('a');
          link.href = data.data.download_url;
          link.download = data.data.filename; // "ressources_jemelabellise.zip"
          link.click();
        } else {
          alert(data.data?.message || 'Erreur lors de la création du ZIP');
        }
      } catch (error) {
        console.error('Error creating ZIP:', error);
        alert('Erreur lors de la création du ZIP');
      } finally {
        // Restaure l'état du bouton
        cartDownload.innerHTML = originalText;
        cartDownload.disabled = cart.length === 0;
      }
    });
  }

  // ===========================================================================
  // RECHERCHE - Filtre les documents par titre
  // ===========================================================================
  if (searchInput) {
    searchInput.addEventListener('input', () => {
      const searchTerm = searchInput.value.toLowerCase().trim();
      const documentCards = page.querySelectorAll('.resources-page__document-card');
      const critereAccordions = page.querySelectorAll('.resources-page__critere-accordion');

      // Affiche/cache chaque document selon qu'il correspond à la recherche
      documentCards.forEach((card) => {
        const title = card.dataset.documentTitle.toLowerCase();
        const matches = searchTerm === '' || title.includes(searchTerm);
        card.style.display = matches ? '' : 'none';
      });

      // Cache les accordéons qui n'ont plus de documents visibles
      critereAccordions.forEach((accordion) => {
        const visibleCards = accordion.querySelectorAll(
          '.resources-page__document-card:not([style*="display: none"])'
        );
        accordion.style.display = visibleCards.length > 0 ? '' : 'none';

        // Ouvre automatiquement l'accordéon s'il a des résultats
        if (searchTerm !== '' && visibleCards.length > 0) {
          accordion.classList.add('resources-page__critere-accordion--open');
          const header = accordion.querySelector('.resources-page__critere-header');
          if (header) header.setAttribute('aria-expanded', 'true');
        }
      });

      // Cache les catégories qui n'ont plus d'accordéons visibles
      const categories = page.querySelectorAll('.resources-page__category');
      categories.forEach((category) => {
        const visibleAccordions = category.querySelectorAll(
          '.resources-page__critere-accordion:not([style*="display: none"])'
        );
        category.style.display = visibleAccordions.length > 0 ? '' : 'none';
      });
    });
  }

  // ===========================================================================
  // FILTRE - Filtre par catégorie de critère
  // ===========================================================================

  // Ouvre/ferme le dropdown des filtres
  if (filterToggle && filterDropdown) {
    filterToggle.addEventListener('click', () => {
      const isOpen = filterToggle.getAttribute('aria-expanded') === 'true';

      if (isOpen) {
        filterToggle.setAttribute('aria-expanded', 'false');
        filterDropdown.setAttribute('hidden', '');
      } else {
        filterToggle.setAttribute('aria-expanded', 'true');
        filterDropdown.removeAttribute('hidden');
      }
    });

    // Ferme le dropdown quand on clique ailleurs
    document.addEventListener('click', (event) => {
      if (!filterToggle.contains(event.target) && !filterDropdown.contains(event.target)) {
        filterToggle.setAttribute('aria-expanded', 'false');
        filterDropdown.setAttribute('hidden', '');
      }
    });
  }

  // Gère le changement des checkboxes de filtre
  filterCheckboxes.forEach((checkbox) => {
    checkbox.addEventListener('change', () => {
      // Récupère toutes les catégories cochées
      const checkedCategories = Array.from(filterCheckboxes)
        .filter((cb) => cb.checked)
        .map((cb) => cb.value);

      // Affiche/cache les catégories selon les filtres actifs
      const categories = page.querySelectorAll('.resources-page__category');
      categories.forEach((category) => {
        const categorySlug = category.dataset.category;
        category.style.display = checkedCategories.includes(categorySlug) ? '' : 'none';
      });
    });
  });

  // ===========================================================================
  // INITIALISATION - Restaure les sélections et met à jour l'interface
  // ===========================================================================
  restoreSelections();
  updateCartUI();

  // Ouvre le panier au chargement de la page
  if (cartElement && cartContent && cartToggle) {
    cartElement.classList.add('resources-page__cart--open');
    cartToggle.setAttribute('aria-expanded', 'true');
    cartContent.removeAttribute('hidden');
  }

  // Ouvre le premier accordéon au chargement
  const firstAccordion = page.querySelector('.resources-page__critere-accordion');
  if (firstAccordion) {
    firstAccordion.classList.add('resources-page__critere-accordion--open');
    const firstHeader = firstAccordion.querySelector('.resources-page__critere-header');
    if (firstHeader) firstHeader.setAttribute('aria-expanded', 'true');
  }
};

// =============================================================================
// CRITÈRE SCROLL PERSISTENCE - Sauvegarde/restaure le scroll sur navigation sidebar
// =============================================================================

const initCritereScrollPersistence = () => {
  if (!document.querySelector('.single-critere')) return;

  const critereLinks = document.querySelectorAll(
    '.criteres-sidebar-navigation__sublink'
  );

  critereLinks.forEach((link) => {
    link.addEventListener('click', () => {
      const scrollData = {
        scrollY: window.scrollY,
        timestamp: Date.now(),
      };
      sessionStorage.setItem('critere-scroll-position', JSON.stringify(scrollData));
    });
  });
};

const restoreCritereScrollPosition = () => {
  if (!document.querySelector('.single-critere')) return;

  const storedData = sessionStorage.getItem('critere-scroll-position');
  if (!storedData) return;

  try {
    const scrollData = JSON.parse(storedData);

    // Vérifier que le timestamp est récent (< 5 min)
    if (Date.now() - scrollData.timestamp > 5 * 60 * 1000) {
      sessionStorage.removeItem('critere-scroll-position');
      return;
    }

    requestAnimationFrame(() => {
      window.scrollTo({
        top: scrollData.scrollY,
        behavior: 'auto',
      });
    });

    sessionStorage.removeItem('critere-scroll-position');
  } catch (error) {
    sessionStorage.removeItem('critere-scroll-position');
  }
};

// =============================================================================
// SIDEBAR CRITÈRES ACCORDION - Ouverture/fermeture des catégories
// =============================================================================
// Permet d'ouvrir/fermer les catégories du sidebar via le bouton toggle (chevron)
// Le lien texte continue de naviguer vers la page catégorie
// =============================================================================

const initCriteresAccordion = () => {
  const headers = document.querySelectorAll('.criteres-sidebar-navigation__category-header');

  if (headers.length === 0) return;

  headers.forEach((header) => {
    header.addEventListener('click', () => {
      const item = header.closest('.criteres-sidebar-navigation__item');
      if (!item) return;

      item.classList.toggle('criteres-sidebar-navigation__item--expanded');
    });
  });
};

// =============================================================================
// SIDEBAR CRITÈRES DRAWER - Drawer mobile pour le sidebar critères
// =============================================================================
// Sur mobile, le sidebar est masqué et s'ouvre via un bouton en drawer latéral
// =============================================================================

const initCriteresSidebarDrawer = () => {
  const toggleBtn = document.querySelector('.criteres-sidebar-toggle');
  const sidebar = document.querySelector('.single-critere__sidebar, .criteres-archive__sidebar');
  const closeBtn = document.querySelector('.criteres-sidebar-close');
  const overlay = document.querySelector('.criteres-sidebar-overlay');

  if (!toggleBtn || !sidebar) return;

  const openDrawer = () => {
    sidebar.classList.add('single-critere__sidebar--open', 'criteres-archive__sidebar--open');
    overlay?.classList.add('criteres-sidebar-overlay--visible');
    toggleBtn.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  };

  const closeDrawer = () => {
    sidebar.classList.remove('single-critere__sidebar--open', 'criteres-archive__sidebar--open');
    overlay?.classList.remove('criteres-sidebar-overlay--visible');
    toggleBtn.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  };

  toggleBtn.addEventListener('click', openDrawer);
  closeBtn?.addEventListener('click', closeDrawer);
  overlay?.addEventListener('click', closeDrawer);

  // Fermer avec Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && sidebar.classList.contains('single-critere__sidebar--open')) {
      closeDrawer();
    }
    if (e.key === 'Escape' && sidebar.classList.contains('criteres-archive__sidebar--open')) {
      closeDrawer();
    }
  });

  // Fermer le drawer si on clique sur un lien dans le sidebar (navigation)
  sidebar.querySelectorAll('a').forEach((link) => {
    link.addEventListener('click', () => {
      closeDrawer();
    });
  });
};

// =============================================================================
// TRACKER TOOLTIP - Affiche une bulle "Mon suivi" à la première connexion
// Réapparaît si l'utilisateur ne s'est pas connecté depuis 30 jours
// Pour restreindre aux "program partners" : vérifier un attribut data- sur le
// tooltip (ex: data-show="true") généré côté PHP selon le rôle OIDC
// =============================================================================

const initTrackerTooltip = () => {
  const tooltip = document.getElementById('tracker-tooltip');
  if (!tooltip) return;

  const wrapper = tooltip.closest('.auth-nav__tracker-wrapper');

  // Déplacer le tooltip au niveau du body pour sortir du contexte de superposition du header
  const backdrop = document.createElement('div');
  backdrop.className = 'tracker-tooltip-backdrop';
  document.body.appendChild(backdrop);
  document.body.appendChild(tooltip);

  const STORAGE_KEY = 'tracker_tooltip_dismissed';
  const DELAY_MS = 30 * 24 * 60 * 60 * 1000; // 30 jours

  const dismissed = localStorage.getItem(STORAGE_KEY);
  const shouldShow = !dismissed || (Date.now() - parseInt(dismissed, 10) > DELAY_MS);

  if (!shouldShow) return;

  const positionTooltip = () => {
    const rect = wrapper.getBoundingClientRect();
    tooltip.style.top = (rect.bottom + 12) + 'px';
    tooltip.style.right = (window.innerWidth - rect.right) + 'px';
  };

  const show = () => {
    positionTooltip();
    tooltip.classList.add('tracker-tooltip--visible');
    backdrop.classList.add('tracker-tooltip-backdrop--visible');
    document.body.classList.add('tracker-tooltip-open');
  };

  setTimeout(show, 800);

  const dismiss = () => {
    tooltip.classList.remove('tracker-tooltip--visible');
    backdrop.classList.remove('tracker-tooltip-backdrop--visible');
    document.body.classList.remove('tracker-tooltip-open');
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
  };

  tooltip.querySelector('.tracker-tooltip__close')?.addEventListener('click', dismiss);
  backdrop.addEventListener('click', dismiss);
};

// =============================================================================
// LOGIN REQUIRED NOTICE - Disparition automatique après 10 secondes
// =============================================================================

const initLoginRequiredNotice = () => {
  const notice = document.querySelector('.login-required-notice');
  if (!notice) return;

  setTimeout(() => {
    notice.classList.add('login-required-notice--hiding');
    notice.addEventListener('animationend', () => notice.remove(), { once: true });
  }, 10000);
};

// =============================================================================
// POINT D'ENTRÉE - Initialise toutes les fonctionnalités au chargement du DOM
// =============================================================================
document.addEventListener('DOMContentLoaded', () => {
  initMobileMenu();        // Menu mobile avec sous-menus
  initMegaMenu();          // Mega menu desktop
  initCertificationAccordion(); // Accordéon bloc certification
  initStickyHeader();      // Header sticky au scroll
  initActionChecklist();   // Bloc checklist avec onglets
  initResourcesPage();     // Page ressources complète
  initCritereScrollPersistence();  // Sauvegarde scroll sidebar critère
  restoreCritereScrollPosition();  // Restaure scroll si navigation sidebar
  initCriteresAccordion(); // Accordéon sidebar catégories critères
  initCriteresSidebarDrawer(); // Drawer mobile sidebar critères
  initTrackerTooltip();    // Tooltip "Mon suivi" première connexion
  initLoginRequiredNotice(); // Notice "connexion requise" — disparaît après 10s
});

import './labellisation-tracker.js';
